/**
 * Staff chat hub events (SR-14) and the event-to-cache reducer.
 *
 * Hub: the existing `/api/notifications/NotificationsHub`. Events go to each participant's own connections only.
 *   StaffChatMessage { threadId, message }      a message posted (also to the sender's other tabs) or deleted
 *   StaffChatRead    { threadId, userId, readAt } a participant marked the thread read
 *   StaffChatUnread  { total }                  the receiving user's unread total changed
 *   StaffChatThread  { threadId }               a thread the user is in was created (refetch it)
 *
 * PRIVACY: nothing here logs an event; a payload carries message bodies.
 */
import type { QueryClient } from '@tanstack/react-query';

import { mapMessage, type Raw, type StaffChatMessage } from '../staff-chat-types';
import { fetchThread } from '../services/staff-chat-api';
import {
  mergeReadMarker,
  staffChatKeys,
  upsertThread,
  writeMessage,
  writeThreadRead,
  type ReadMarkers,
  type ThreadsData,
} from '../services/staff-chat-cache';

export const STAFF_CHAT_EVENTS = {
  message: 'StaffChatMessage',
  read: 'StaffChatRead',
  unread: 'StaffChatUnread',
  thread: 'StaffChatThread',
} as const;

export type StaffChatEvent =
  | { type: 'message'; threadId: string; message: StaffChatMessage }
  | { type: 'read'; threadId: string; userId: string; readAt: string }
  | { type: 'unread'; total: number }
  | { type: 'thread'; threadId: string };

/** SignalR hands the payload as the first argument, sometimes wrapped in an array. */
function firstPayload(args: unknown[]): Raw | null {
  const head = Array.isArray(args[0]) ? (args[0] as unknown[])[0] : args[0];
  return typeof head === 'object' && head !== null ? (head as Raw) : null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/** Reads one hub event; null when the payload is not the contract's shape. */
export function readStaffChatEvent(name: string, args: unknown[]): StaffChatEvent | null {
  const p = firstPayload(args);
  if (!p) return null;
  switch (name) {
    case STAFF_CHAT_EVENTS.message: {
      const threadId = str(p.threadId);
      const raw = typeof p.message === 'object' && p.message !== null ? (p.message as Raw) : null;
      if (!threadId || !raw) return null;
      const message = mapMessage(raw);
      if (!message.id) return null;
      return { type: 'message', threadId, message: { ...message, threadId: message.threadId || threadId } };
    }
    case STAFF_CHAT_EVENTS.read: {
      const threadId = str(p.threadId);
      const userId = str(p.userId);
      if (!threadId || !userId) return null;
      return { type: 'read', threadId, userId, readAt: str(p.readAt) ?? '' };
    }
    case STAFF_CHAT_EVENTS.unread:
      return typeof p.total === 'number' && Number.isFinite(p.total) ? { type: 'unread', total: Math.max(0, p.total) } : null;
    case STAFF_CHAT_EVENTS.thread: {
      const threadId = str(p.threadId);
      return threadId ? { type: 'thread', threadId } : null;
    }
    default:
      return null;
  }
}

export interface ApplyDeps {
  /** Reads a thread for `StaffChatThread` (the event carries the id only). */
  loadThread?: (threadId: string) => Promise<unknown>;
}

/** Applies one event to the TanStack Query caches. */
export function applyStaffChatEvent(
  qc: QueryClient,
  event: StaffChatEvent,
  meUserId: string | null | undefined,
  deps: ApplyDeps = {},
): void {
  switch (event.type) {
    case 'message':
      writeMessage(qc, event.message, meUserId);
      return;
    case 'read':
      // The caller's own read (another tab) clears the counts; a colleague's read is the "Seen" marker.
      if (event.userId === meUserId) writeThreadRead(qc, event.threadId);
      else if (event.readAt)
        qc.setQueryData<ReadMarkers>(staffChatKeys.reads(event.threadId), (d) => mergeReadMarker(d, event.userId, event.readAt));
      return;
    case 'unread':
      qc.setQueryData<number>(staffChatKeys.unread(), event.total);
      return;
    case 'thread': {
      const load =
        deps.loadThread ??
        (async (threadId: string) => {
          const thread = await fetchThread(threadId);
          qc.setQueryData(staffChatKeys.thread(threadId), thread);
          qc.setQueryData<ThreadsData>(staffChatKeys.threads(), (d) => upsertThread(d, thread));
        });
      void load(event.threadId).catch(() => {
        void qc.invalidateQueries({ queryKey: staffChatKeys.threads() });
      });
      return;
    }
  }
}
